import { useEffect, useState } from "react";
import SearchCard from "../../components/cards/SearchCard";
import SectionHeading from "../../components/heading/SectionHeading";
import {
  courseCardData,
  type ScholarshipData,
} from "../../content/scholarship-course-page";
import { getScholarshipsReq, getAllSavedResources } from "../../utils/api";
import type {
  UniversityData,
  UniversityDetails,
} from "../../types/universityDetails";
import {
  UniversityIcon,
  SaveIcon,
  ActionIcon,
  ShareIcon,
  ArrowRightAltIcon,
  SaveIconActive,
} from "../../common/icons";
import { CompareModal, type Scholarship } from "../modal-button/Compare";
import type { SavedScholarshipItem } from "../../content/saved-page-scholarship";

const UniversityScholarships = ({
  uniDetails,
}: {
  uniDetails: UniversityData | UniversityDetails;
}) => {
  const { defaultImage, labels } = courseCardData;

  const [courses, setCourses] = useState<ScholarshipData[]>([]);
  const [SavedScholarshipsIds, setSavedScholarshipsIds] = useState<Set<number>>(
    new Set(),
  );
  const [refresh, setRefresh] = useState(false);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [selectedScholarships, setSelectedScholarships] = useState<
    Scholarship[]
  >([]);

  const getCourses = async () => {
    try {
      const params = { institutes: uniDetails.slug };
      const data = await getScholarshipsReq(params);

      setCourses(data.results || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  async function getSavedScholarships() {
    try {
      const response = await getAllSavedResources("scholarship");
      const savedIds: Set<number> = new Set(
        response.scholarships.map(
          (scholarship: SavedScholarshipItem) => scholarship.scholarship.id,
        ),
      );
      setSavedScholarshipsIds(savedIds);
    } catch (error) {
      console.error("Error fetching saved scholarships:", error);
    }
  }

  const handleRemove = () => {
    setRefresh((prev) => !prev);
  };

  const handleSave = () => {
    setRefresh((prev) => !prev);
  };

  const truncateText = (text: string, limit: number) => {
    if (text.length <= limit) return text;
    return `${text.slice(0, limit)}...`;
  };

  const handleCompareClick = (scholarship: ScholarshipData) => {
    // Format the scholarship according to the expected interface
    const scholarshipToAdd: Scholarship = {
      id: scholarship.id,
      scholarship_name: scholarship.scholarship_name || "Unknown Scholarship",
      institute: {
        id: scholarship.institute?.id || 0,
        name: scholarship.institute?.institute_name || "Unknown Institute",
        logo: scholarship.institute?.logo || defaultImage,
      },
      slug: scholarship.slug || "",
    };

    // Check for duplicates
    const isDuplicate = selectedScholarships.some(
      (sch) => sch.id === scholarshipToAdd.id,
    );

    if (isDuplicate) {
      alert("This scholarship is already selected for comparison");
      return;
    }

    // Check maximum limit
    if (selectedScholarships.length >= 3) {
      alert("You can compare maximum 3 scholarships");
      return;
    }

    // Add to selected scholarships and open modal
    setSelectedScholarships([...selectedScholarships, scholarshipToAdd]);
    setIsCompareModalOpen(true);
  };

  const getCardConfig = (course: ScholarshipData) => {
    const isAlreadySaved =
      course.id !== undefined && SavedScholarshipsIds.has(course.id);
    return {
      icon: UniversityIcon,
      pageType: "scholarship" as const,
      detailsConfig: [
        {
          label: labels.degreeLevel,
          value: course.degree_level.map((level) => level.name).join(", "),
          key: "degree",
        },
        {
          label: labels.type,
          value:
            course.scholarship_type
              .map((type) => type.display_name)
              .join(", ") || "N/A",
          key: "type",
        },
        {
          label: labels.value,
          value: truncateText(course.scholarship_value || "N/A", 10) || "N/A",
          key: "value",
        },
        {
          label: labels.intake,
          value: course.intakes.map((item) => item.year).join(", ") || "N/A",
          key: "intake",
        },
      ],
      buttons: {
        primary: {
          label: "Apply now",
          href: "https://timesconsultant.com/contact/",
        },
        secondary: {
          label: labels.viewDetail,
          href: `/scholarship/${course.slug}`,
        },
      },
      actionIcons: [
        isAlreadySaved ? SaveIconActive : SaveIcon,
        ActionIcon,
        ShareIcon,
      ],
    };
  };

  useEffect(() => {
    getCourses();
    getSavedScholarships();
  }, [uniDetails.slug]);

  useEffect(() => {
    if (refresh) {
      getSavedScholarships();
      setRefresh(false);
    }
  }, [refresh]);

  const handleSeeMore = () => {
    const params = new URLSearchParams({
      institutes: uniDetails.slug,
    });

    // Navigate to course search page
    window.location.href = `/scholarship-search?${params.toString()}`;
  };

  const visibleCourses = courses.length > 3 ? courses.slice(0, 3) : courses;

  return courses.length > 0 ? (
    <div className="mb-2">
      <div className="px-4">
        <SectionHeading
          title={
            "sections" in uniDetails && uniDetails.sections?.[3]?.title
              ? uniDetails.sections[3].title
              : "institute_name" in uniDetails
                ? `${uniDetails.institute_name} Scholarships`
                : ""
          }
          paragraph={
            "sections" in uniDetails &&
            uniDetails.sections?.[3]?.body_text_content
              ? uniDetails.sections[3].body_text_content
              : ""
          }
        />
      </div>
      {visibleCourses.map((course, i) => (
        <SearchCard
          key={i}
          handleCompareClick={() => handleCompareClick(course)}
          data={{
            name: course.scholarship_name || "Unknown",
            institute_name: course.institute?.institute_name || "N/A",
            logo: course.institute?.logo || defaultImage,
            slug: course.slug,
            institute_slug: uniDetails.slug,
            id: course.id,
            type: "scholarship",
          }}
          config={getCardConfig(course)}
          defaultImage={defaultImage}
          gridClassName="grid-cols-3 xl:grid-cols-4"
          isAlreadySaved={
            course.id !== undefined && SavedScholarshipsIds.has(course.id)
          }
          onRemove={handleRemove}
          onSave={handleSave}
          variant="uni-detail"
        />
      ))}

      {courses.length > 4 && (
        <div
          className="flex px-0 md:px-4 justify-end items-center mt-3 cursor-pointer text-primary"
          onClick={handleSeeMore}
        >
          <ArrowRightAltIcon />
          <span className="ml-1">{"See More"}</span>
        </div>
      )}

      <CompareModal
        open={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
        selectedEntities={selectedScholarships}
        entityType="scholarship"
        setSelectedEntities={setSelectedScholarships}
      />
    </div>
  ) : null;
};

export default UniversityScholarships;
